/**
 * Typed calls to the scheduling API under /api. The screens use these and
 * nothing else; identity is attached by useApi (see ../auth/identity.ts).
 */
import type {
  ApprovalReview, CalendarMonth, CompanyEventKind, CompanyEventView, DeploymentDetail, DeploymentSaved, DeploymentView, Evaluation, MyRequest, NtdNotifyEntry, PayrollDryRun, PayrollLoaded,
  PendingDecision, PersonRef, ProjectOption, PersonSummary, ReportImageType, RequestDraft, StatusChange, ThemeChoice, UnpaidCandidate, UnpaidCreated, WfhMonth, WfhPortion,
} from "@scheduler/shared";
import { useMemo } from "react";

import { useApi } from "../auth/useApi";

export type Draft = RequestDraft & { title?: string; explanation?: string | null };

/** POST /api/deployments. Empty text boxes are sent as null. */
export interface DeploymentDraftBody {
  title: string;
  projectAjeraKey: number;
  start: string;
  end: string;
  crewIds: string[];
  site: string | null;
  explanation: string | null;
  /** The PM by EE Key, pre-filled with the job's PM; null for none (27 Oct 2026). */
  pmAjeraKey: number | null;
}

/** PATCH /api/deployments/:id: what changed, and the version the page read. */
export type DeploymentPatchBody = { version: number } & Partial<Omit<DeploymentDraftBody, "projectAjeraKey">>;

/** An API refusal, with the machine-readable code and a message for people. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

/** Rows in each work table, by table name (api/src/db/work-data.ts, WORK_TABLES). */
export type WorkCounts = Record<string, number>;

export interface ReportCategory {
  readonly id: string;
  readonly label: string;
}

export type SubmitOutcome =
  /** `waitingOn`: who decides it, a name or "HR"; null when approved on submit. */
  | { ok: true; requestId: string; status: "PENDING" | "APPROVED"; evaluation: Evaluation; cancelled: string[]; waitingOn: string | null }
  | { ok: false; evaluation: Evaluation };

/** Survives a page change; private windows throw, and that is not fatal. */
function rememberReference(reference: string): void {
  try {
    window.sessionStorage.setItem("scheduler:lastErrorReference", reference);
  } catch {
    // Blocked site data. A report without a reference is still a report.
  }
}

async function readJson<T>(response: Response): Promise<T> {
  const body = (await response.json().catch(() => null)) as
    | (T & { error?: string; message?: string; reference?: string })
    | null;
  if (!response.ok) {
    // So a problem report can carry the id that finds the log line, without
    // anybody copying it out of an error message.
    if (body?.reference) rememberReference(body.reference);
    throw new ApiError(response.status, body?.error ?? "HTTP_ERROR", body?.message ?? `The API returned ${response.status}.`);
  }
  return body as T;
}

const json = (body: unknown): RequestInit => ({
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(body),
});

const upload = (file: File): RequestInit => ({
  method: "POST",
  headers: { "Content-Type": "application/octet-stream" },
  body: file,
});

export function useSchedulerApi() {
  const call = useApi();

  return useMemo(
    () => ({
      me: async () => readJson<PersonSummary>(await call("/api/people/me")),
      people: async () => readJson<PersonRef[]>(await call("/api/people")),
      /** Active Deltek projects to book field work against, each with the PM who will approve it. */
      projects: async () => readJson<ProjectOption[]>(await call("/api/projects")),
      calendar: async (month: string, activeOnly: boolean) =>
        readJson<CalendarMonth>(
          await call(`/api/calendar?month=${encodeURIComponent(month)}&activeOnly=${activeOnly}`),
        ),
      /** Working from home: everyone's days in a month, for the tab and the calendar. */
      wfhMonth: async (month: string) => readJson<WfhMonth>(await call(`/api/wfh?month=${encodeURIComponent(month)}`)),
      wfhSet: async (date: string, portion: WfhPortion) =>
        readJson<{ date: string; portion: WfhPortion }>(await call(`/api/wfh/${encodeURIComponent(date)}`, {
          method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ portion }),
        })),
      wfhClear: async (date: string) =>
        readJson<{ date: string; portion: null }>(await call(`/api/wfh/${encodeURIComponent(date)}`, { method: "DELETE" })),
      /** Light / Dark / System, kept by Canopy (S13). `available: false`: Canopy did not answer. */
      myTheme: async () => readJson<{ theme: ThemeChoice; available: boolean }>(await call("/api/me/theme")),
      setMyTheme: async (theme: ThemeChoice) =>
        readJson<{ theme: ThemeChoice }>(await call("/api/me/theme", {
          method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ theme }),
        })),
      myRequests: async () => readJson<MyRequest[]>(await call("/api/requests/mine")),
      // Field work as a DEPLOYMENT (21 Oct 2026): its own record, edited in place.
      /** Every live deployment, not only the caller's — field work is a shared fact. */
      deployments: async () => readJson<DeploymentView[]>(await call("/api/deployments")),
      deployment: async (id: string) =>
        readJson<DeploymentDetail>(await call(`/api/deployments/${encodeURIComponent(id)}`)),
      /** Booked on save. `told` / `toldPm`: who was queued to be told (never the caller). */
      createDeployment: async (draft: DeploymentDraftBody) =>
        readJson<DeploymentSaved>(await call("/api/deployments", json(draft))),
      /** `version` is the one the page read; a newer one now answers 409 STALE. */
      updateDeployment: async (id: string, patch: DeploymentPatchBody) =>
        readJson<DeploymentSaved>(await call(`/api/deployments/${encodeURIComponent(id)}`, { ...json(patch), method: "PATCH" })),
      cancelDeployment: async (id: string, reason: string) =>
        readJson<DeploymentSaved>(await call(`/api/deployments/${encodeURIComponent(id)}/cancel`, json({ reason }))),
      addDeploymentNote: async (id: string, body: string) =>
        readJson<DeploymentDetail>(await call(`/api/deployments/${encodeURIComponent(id)}/notes`, json({ body }))),
      /** The deployment an old /field/<request id> link's request became. */
      deploymentForRequest: async (requestId: string) =>
        (await readJson<{ deploymentId: string }>(await call(`/api/field/${encodeURIComponent(requestId)}`))).deploymentId,
      preview: async (draft: Draft) =>
        (await readJson<{ evaluation: Evaluation }>(await call("/api/requests/preview", json(draft)))).evaluation,
      submit: async (draft: Draft): Promise<SubmitOutcome> => {
        const response = await call("/api/requests", json(draft));
        if (response.status === 422) {
          const body = (await response.json()) as { evaluation: Evaluation };
          return { ok: false, evaluation: body.evaluation };
        }
        return readJson<SubmitOutcome>(response);
      },
      cancel: async (requestId: string) =>
        readJson<{ changes: StatusChange[] }>(
          await call(`/api/requests/${encodeURIComponent(requestId)}/cancel`, { method: "POST" }),
        ),
      pending: async () => readJson<PendingDecision[]>(await call("/api/requests/pending")),
      review: async (requestId: string) =>
        readJson<ApprovalReview>(await call(`/api/requests/${encodeURIComponent(requestId)}/review`)),
      approve: async (requestId: string) =>
        readJson<{ changes: StatusChange[]; decidedAs: string }>(
          await call(`/api/requests/${encodeURIComponent(requestId)}/approve`, { method: "POST" }),
        ),
      /** The reason is required, and the person is told it. */
      deny: async (requestId: string, reason: string) =>
        readJson<{ changes: StatusChange[]; decidedAs: string }>(
          await call(`/api/requests/${encodeURIComponent(requestId)}/deny`, json({ reason })),
        ),

      // HR only. The API refuses everyone else with 403, whatever the screen shows.
      // The file goes up as it is. It is parsed in memory on the server and not stored.
      payrollDryRun: async (file: File) =>
        readJson<PayrollDryRun>(await call(`/api/hr/payroll/dry-run?name=${encodeURIComponent(file.name)}`, upload(file))),
      payrollConfirm: async (file: File, code: string) =>
        readJson<PayrollLoaded>(
          await call(`/api/hr/payroll/confirm?name=${encodeURIComponent(file.name)}&code=${encodeURIComponent(code)}`, upload(file)),
        ),
      // Company events (S25, S26): HR only, refused while acting, like every HR tool.
      companyEvents: async () => readJson<CompanyEventView[]>(await call("/api/hr/company-events")),
      companyEventAdd: async (body: { name: string; firstDay: string; lastDay: string; kind: CompanyEventKind }) =>
        readJson<CompanyEventView[]>(await call("/api/hr/company-events", json(body))),
      companyEventRemove: async (id: string) =>
        readJson<CompanyEventView[]>(await call(`/api/hr/company-events/${encodeURIComponent(id)}`, { method: "DELETE" })),
      ntdNotifyList: async () => readJson<NtdNotifyEntry[]>(await call("/api/hr/ntd-notify")),
      ntdNotifyAdd: async (resourceId: string) =>
        readJson<NtdNotifyEntry[]>(await call("/api/hr/ntd-notify", json({ resourceId }))),
      ntdNotifyRemove: async (ajeraKey: string) =>
        readJson<NtdNotifyEntry[]>(await call(`/api/hr/ntd-notify/${encodeURIComponent(ajeraKey)}`, { method: "DELETE" })),
      // HR acting as someone: the bracket in the admin log, made as the real HR person.
      actAsStart: async (ajeraKey: string, note: string) =>
        readJson<PersonRef>(await call("/api/act-as", json({ ajeraKey, note }), { asSelf: true })),
      actAsStop: async (ajeraKey: string, note: string) =>
        readJson<unknown>(await call("/api/act-as/stop", json({ ajeraKey, note }), { asSelf: true })),

      /** Who HR may give unpaid time off: never a part-timer. */
      unpaidPeople: async () => readJson<UnpaidCandidate[]>(await call("/api/hr/unpaid/people")),
      /** Dev only: whether this environment may load training data (api/src/hr/training-data.ts). */
      trainingDataEnabled: async () =>
        (await readJson<{ enabled: boolean }>(await call("/api/hr/training-data"))).enabled,
      trainingDataLoad: async () =>
        readJson<{ balancesAdded: number; requests: number; deployments: number; refused: string[] }>(
          await call("/api/hr/training-data", json({ confirm: "LOAD" }))),
      /**
       * The work reset (api/src/hr/work-reset.ts): enabled only while the App Service carries
       * ALLOW_WORK_RESET, with what a reset would remove now. HR only, and never while acting.
       */
      workResetStatus: async () =>
        readJson<{ enabled: boolean; counts: WorkCounts | null; demoSeedCanRefill: boolean }>(await call("/api/hr/work-reset")),
      workReset: async (confirm: string) =>
        readJson<{ before: WorkCounts; after: WorkCounts; demoSeedCanRefill: boolean }>(
          await call("/api/hr/work-reset", json({ confirm }))),
      /** Created already approved. A gate failure comes back as the evaluation, like submit. */
      unpaidCreate: async (input: { resourceId: string; start: string; end: string; perDay?: Record<string, number>; note: string | null }):
        Promise<{ ok: true; created: UnpaidCreated } | { ok: false; evaluation: Evaluation }> => {
        const response = await call("/api/hr/unpaid", json(input));
        if (response.status === 422) return { ok: false, evaluation: ((await response.json()) as { evaluation: Evaluation }).evaluation };
        return { ok: true, created: await readJson<UnpaidCreated>(response) };
      },
      /** Jury duty: HR has seen the summons. A tick, never a file. */
      summonsReceived: async (requestId: string) =>
        readJson<{ at: string; by: string }>(await call(`/api/hr/requests/${encodeURIComponent(requestId)}/summons`, { method: "POST" })),

      reportCategories: async () =>
        (await readJson<{ categories: ReportCategory[] }>(await call("/api/report-categories"))).categories,
      reportProblem: async (input: {
        categoryId: string;
        body: string;
        route?: string | null;
        reference?: string | null;
        appBuild?: string | null;
        /** Screenshots, as base64 (3 Nov 2026): up to five, shrunk first (reportImages.ts). */
        images?: { contentType: ReportImageType; data: string }[];
      }) => readJson<unknown>(await call("/api/reports", json(input))),
    }),
    [call],
  );
}
